import type { ReactNode } from 'react';

export type DataTableColumn<T> = {
  /** Stable identifier used as React key + test handle. */
  key: string;
  /** Visible column header. */
  header: string;
  /** Cell renderer. Receives the row; must return a React node. */
  cell: (row: T) => ReactNode;
  /** Text alignment for both header and cells. Defaults to "left". */
  align?: 'left' | 'right' | 'center';
  /** Optional fixed width. Accepts any valid CSS width. */
  width?: string | number;
  /** If true, the cell uses monospace tabular numbers. */
  numeric?: boolean;
};

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  rows: ReadonlyArray<T>;
  /**
   * Stable key extractor. Defaults to `String(index)` so generic use
   * never throws, but every real call site should pass an explicit
   * function tied to the row's primary id.
   */
  rowKey?: (row: T, index: number) => string;
  /** Rendered inside an empty `<tbody>` when `rows` is empty. */
  emptyMessage?: string;
  /** Optional caption rendered above the table (accessibility hint). */
  caption?: string;
};

/**
 * Generic data table for the EXCEL HR demo pages.
 *
 * The component is purely presentational and props-driven:
 * - Callers declare the columns (header + cell renderer).
 * - Callers provide an optional `rowKey` extractor so React can
 *   reconcile rows stably across re-renders.
 * - Empty state is configurable via `emptyMessage`.
 *
 * Styling lives in `.axi-data-table*` rules in `dashboard.css`.
 * The component intentionally does not import any Ant Design /
 * react-table dependency — the dark-gold dashboard chrome uses
 * the same handcrafted table style throughout.
 */
function DataTable<T>({
  columns,
  rows,
  rowKey,
  emptyMessage = '暂无数据',
  caption
}: DataTableProps<T>) {
  return (
    <div className="axi-data-table-wrap">
      <table className="axi-data-table">
        {caption ? <caption className="axi-data-table__caption">{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="axi-data-table__th"
                style={{
                  textAlign: col.align ?? 'left',
                  width: col.width
                }}
                data-col={col.key}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr className="axi-data-table__empty">
              <td colSpan={columns.length}>{emptyMessage}</td>
            </tr>
          ) : (
            rows.map((row, idx) => {
              const key = rowKey ? rowKey(row, idx) : String(idx);
              return (
                <tr key={key} className="axi-data-table__row">
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={
                        'axi-data-table__td' +
                        (col.numeric ? ' is-numeric' : '')
                      }
                      style={{ textAlign: col.align ?? 'left' }}
                      data-col={col.key}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;